import { useEffect } from "react";
import { Stack, Typography, Chip } from "@mui/material";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import { CargoScanReading, ScanReading } from "dora-contracts";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useSelector } from "../../../../redux-store";
import { loadScanReadings } from "../../../../ducks/data/scan-readings";
import {
  selectScanReadings,
  selectScanReadingsFailed,
} from "../../../../ducks/data/scan-readings/selectors";
import { useL10n } from "../../../../l10n";

type L10n = ReturnType<typeof useL10n>;

const formatKg = (l10n: L10n, value: string | null | undefined) =>
  value ? `${l10n.formatNumber(value)} kg` : null;

/** The extracted values worth showing, in reading order, empty ones dropped. */
const readingValues = (
  extracted: ScanReading,
  t: TFunction<"shipmentModal">,
  l10n: L10n
): string[] =>
  (
    [
      [t("cards.scanReadings.netWeight"), formatKg(l10n, extracted.netWeightKg)],
      [
        t("cards.scanReadings.grossWeight"),
        formatKg(l10n, extracted.grossWeightKg),
      ],
      [
        t("cards.scanReadings.tareWeight"),
        formatKg(l10n, extracted.tareWeightKg),
      ],
      [
        t("cards.scanReadings.volume"),
        extracted.cubicMeters
          ? `${l10n.formatNumber(extracted.cubicMeters)} m³`
          : null,
      ],
      [
        t("cards.scanReadings.quantity"),
        extracted.quantity
          ? `${l10n.formatNumber(extracted.quantity)}${
              extracted.unit ? ` ${extracted.unit}` : ""
            }`
          : null,
      ],
      [t("cards.scanReadings.ref"), extracted.ref ?? null],
      [t("cards.scanReadings.date"), extracted.date ?? null],
      [t("cards.scanReadings.signedBy"), extracted.signedBy ?? null],
    ] satisfies [string, string | null][]
  )
    .filter(([, value]) => value)
    .map(([label, value]) => `${label}: ${value}`);

const ScanReadingRow = ({ reading }: { reading: CargoScanReading }) => {
  const { t } = useTranslation("shipmentModal");
  const l10n = useL10n();
  const { extracted } = reading;
  if (!extracted) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Chip
          size="small"
          variant="outlined"
          color="primary"
          label={t(`cards.scanReadings.kinds.${extracted.kind}`)}
        />
        <Typography variant="caption" color="text.secondary">
          {l10n.formatDateAndTime(reading.createdAt)}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {readingValues(extracted, t, l10n).join(" · ")}
      </Typography>
      {reading.applyError && (
        <Typography variant="caption" color="warning.main">
          {t("cards.scanReadings.notApplied")}
        </Typography>
      )}
    </div>
  );
};

/**
 * AI readings of driver-uploaded scans (weighbridge slips etc.) on the order.
 * Read-only display of the extracted values - the weight they produce is
 * written as its own cargo event, which is what the log below shows.
 *
 * A cargo with no readings renders nothing, but a failed load says so: the
 * dispatcher uses this panel to check what was read off the driver's slips
 * before invoicing, and an empty panel that silently means "we couldn't ask"
 * would read as "the driver uploaded nothing".
 *
 * Mounted only when the `scan-readings` feature is on - see the order dialog.
 */
const ScanReadingsCard = ({ cargoId }: { cargoId: string }) => {
  const { t } = useTranslation("shipmentModal");
  const dispatch = useAppDispatch();
  const readings = useSelector(selectScanReadings(cargoId));
  const loadFailed = useSelector(selectScanReadingsFailed(cargoId));

  useEffect(() => {
    dispatch(loadScanReadings(cargoId));
  }, [dispatch, cargoId]);

  if (loadFailed) {
    return (
      <Typography variant="caption" color="error">
        {t("cards.scanReadings.loadFailed")}
      </Typography>
    );
  }

  if (readings.length === 0) return null;

  return (
    <div>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, mt: 2 }}>
        <DocumentScannerIcon fontSize="small" />
        <Typography variant="subtitle2">{t("cards.scanReadings.title")}</Typography>
      </Stack>
      {readings.map((reading) => (
        <ScanReadingRow key={reading.id} reading={reading} />
      ))}
    </div>
  );
};

export default ScanReadingsCard;
